// Types and wording for speeding fines on the driving conduct ladder: the Fines
// tab, the driver record's Driving tab, the Traffic Fines panel and the
// driver's own page (db/migrations/0390 and 0480 to 0484).
//
// NOTHING HERE DECIDES ANYTHING. Which bucket a fine is in, whether it is
// reckless, whether it counts and who may act all arrive from the database
// (CLAUDE.md rule 4). This file turns codes into words.
//
// A MISSING FIGURE SHOWS AS MISSING. A speed nobody entered from the notice is
// '—', never 0 (CLAUDE.md rule 6).

// '2026-10-06' -> 'Tue 6 Oct', the same shape as drivingConduct's dayLabel.
// Kept here rather than imported so this file stays loadable by node's test
// runner, which needs an extension that the web build refuses.
function dayLabel(date: string): string {
  const d = new Date(`${String(date).slice(0, 10)}T00:00:00Z`)
  if (Number.isNaN(d.getTime())) return String(date)
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${days[d.getUTCDay()]} ${d.getUTCDate()} ${months[d.getUTCMonth()]}`
}

export type FineBucket = 'awaiting_attribution' | 'awaiting_response' | 'disputed' | 'counted' | 'reckless' | 'not_counted'
export type OffenceCategory = 'speeding' | 'red_light' | 'parking' | 'licence_disc' | 'other'
export type DisputeKind = 'not_the_driver' | 'the_fine'
export type DisputeOutcome = 'no_dispute' | 'dispute_rejected' | 'upheld_not_driver' | 'upheld_fine' | 'withdrawn'
export type ConsentStatus = 'offered' | 'signed' | 'refused' | 'withdrawn'

export type FineRow = {
  fine_id: string
  bucket: FineBucket
  notice_number: string | null
  plate: string | null
  offence_at: string | null
  offence_on: string | null
  offence_time: string | null
  place: string | null
  recorded_kmh: number | null
  limit_kmh: number | null
  over_limit_kmh: number | null
  amount_zar: number | string | null
  reckless: boolean | null
  source: string
  attribution_status: 'attributed' | 'awaiting' | 'unattributed' | null
  unattributed_reason: string | null
  confirmed_driver_name: string | null
  driver_id: string | null
  driver_name: string | null
  manager_name: string | null
  stage: 'notice_pending' | 'awaiting_response' | 'disputed' | 'closed' | null
  notice_given_on: string | null
  notice_method: string | null
  response_due_on: string | null
  dispute_kind: DisputeKind | null
  dispute_text: string | null
  disputed_on: string | null
  dispute_via: 'board' | 'recorded' | null
  outcome: DisputeOutcome | null
  outcome_reasons: string | null
  gate_date: string | null
  conduct_eligible: boolean
  coaching_only_reason: string | null
  case_id: string | null
  case_status: string | null
  counted_in: { window_start: string; window_end: string } | null
  consent: { status: ConsentStatus; offered_on: string; decided_on: string | null; amount_zar: number | string; evidence: string | null } | null
  can_act: boolean
}

export type UnclassifiedRow = {
  fine_id: string
  plate: string | null
  notice_number: string | null
  offence_at: string | null
  sheet_date: string | null
  statement_period: string | null
  amount_zar: number | string | null
  source: string
  offence_description: string | null
  driver_name: string | null
}

export type DrivingFinesPage = {
  as_of: string
  messages_on: boolean
  dispute_working_days: number
  reckless_over_limit_kmh: number
  reckless_absolute_kmh: number
  can_classify: boolean
  counts: Record<FineBucket | 'unclassified', number>
  fines: FineRow[]
  unclassified: UnclassifiedRow[]
  personLinked: boolean
}

export const BUCKETS: { key: FineBucket; label: string; blurb: string }[] = [
  { key: 'awaiting_attribution', label: 'Awaiting attribution', blurb: 'Speeds not entered, driver not confirmed, or the shift record does not put that driver in the car.' },
  { key: 'awaiting_response', label: 'Awaiting driver response', blurb: 'Attributed; the notice is to be given, or the driver is inside the dispute window.' },
  { key: 'disputed', label: 'Disputed', blurb: 'The driver disputes it. The Driver Servant records the outcome before anything else happens.' },
  { key: 'counted', label: 'Confirmed and counted', blurb: 'The driver is confirmed. Counts as a breach week, merged with any Cartrack week in the same 7 days.' },
  { key: 'reckless', label: 'Reckless', blurb: 'Confirmed, and above the clause 21.5 line. Raises a reckless driving case for a hearing, not a ladder step.' },
  { key: 'not_counted', label: 'Not counted', blurb: 'A dispute was upheld.' },
]

export const CATEGORY_OPTIONS: { value: OffenceCategory; label: string }[] = [
  { value: 'speeding', label: 'Speeding' },
  { value: 'red_light', label: 'Red light' },
  { value: 'parking', label: 'Parking' },
  { value: 'licence_disc', label: 'Licence disc' },
  { value: 'other', label: 'Other' },
]

export const UNATTRIBUTED_FINE_LABEL: Record<string, string> = {
  no_offence_time: 'No offence time on the row',
  speeds_not_entered: 'Recorded speed and limit not entered',
  driver_not_confirmed: 'Driver not confirmed on Traffic Fines',
  no_fleetly_driver: 'Confirmed as no Fleetly driver',
  confirmed_driver_not_on_shift: 'Confirmed driver was not on shift in that car',
  no_shift: 'No shift on that car at that time',
  two_drivers: 'Two drivers on shift',
  handover_window: 'In the handover window',
  allocation_disagrees: 'Shift and allocation disagree',
  no_vehicle: 'No vehicle on the fine',
}

export const OUTCOME_LABEL: Record<DisputeOutcome, string> = {
  no_dispute: 'No dispute in the window',
  dispute_rejected: 'Dispute rejected',
  upheld_not_driver: 'Upheld: not the driver',
  upheld_fine: 'Upheld: the fine is wrong',
  withdrawn: 'Withdrawn',
}

export const DISPUTE_KIND_LABEL: Record<DisputeKind, string> = {
  not_the_driver: 'He was not the driver',
  the_fine: 'The fine itself',
}

export const CONSENT_LABEL: Record<ConsentStatus, string> = {
  offered: 'Consent offered, no answer yet',
  signed: 'Consent signed: recovered in payroll',
  refused: 'Consent refused: recorded as owing',
  withdrawn: 'Consent offer withdrawn',
}

/** "92 km/h in a 60, 32 over", or '—' when the notice's speeds are not entered. */
export function fineSpeedLine(f: Pick<FineRow, 'recorded_kmh' | 'limit_kmh' | 'over_limit_kmh'>): string {
  if (f.recorded_kmh === null || f.recorded_kmh === undefined) return '—'
  if (f.limit_kmh === null || f.limit_kmh === undefined) return `${f.recorded_kmh} km/h, limit not entered`
  const over = f.over_limit_kmh ?? f.recorded_kmh - f.limit_kmh
  return `${f.recorded_kmh} km/h in a ${f.limit_kmh}, ${over} over`
}

/** R1,500.00, or '—' for no amount. Never R0.00 for a missing amount. */
export function rand(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—'
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  // The same shape as the Traffic Fines page (utils/fines formatZar): R1,500.00.
  return 'R' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/** Where a fine stands, in one line, from the codes the database sent. */
export function fineStanding(f: Pick<FineRow, 'bucket' | 'unattributed_reason' | 'stage' | 'response_due_on' | 'outcome' | 'conduct_eligible' | 'coaching_only_reason' | 'counted_in' | 'case_status'>): string {
  switch (f.bucket) {
    case 'awaiting_attribution':
      return UNATTRIBUTED_FINE_LABEL[f.unattributed_reason ?? ''] ?? 'Not attributed'
    case 'awaiting_response':
      return f.stage === 'notice_pending' ? 'Notice to be given' : `Dispute window open until ${f.response_due_on ? dayLabel(f.response_due_on) : '—'}`
    case 'disputed':
      return 'Disputed: outcome to be recorded'
    case 'not_counted':
      return OUTCOME_LABEL[f.outcome as DisputeOutcome] ?? 'Not counted'
    case 'counted':
    case 'reckless': {
      if (!f.conduct_eligible) {
        const c = f.coaching_only_reason ?? 'coaching only'
        return c.charAt(0).toUpperCase() + c.slice(1)
      }
      if (f.case_status) return `On a case: ${f.case_status.replace(/_/g, ' ')}`
      if (f.bucket === 'counted' && f.counted_in) return `Counted in the breach week from ${dayLabel(f.counted_in.window_start)}`
      return f.bucket === 'reckless' ? 'A reckless driving case is raised at the next daily run' : 'Counts; a case is raised at the next daily run'
    }
  }
}
